import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import {
  isCompactRichTextField,
  usesRichTextEditor,
} from "../../../utils/richTextField";
import MediaUploadBox from "../media/MediaUploadBox";

function inputClassName() {
  return "mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100";
}

export default function FieldInputRenderer({
  field,
  value,
  onChange,
  onUpload,
  uploadBusy,
}) {
  const type = (field.fieldType || "text").toLowerCase();
  const label = field.label || field.key;

  if (type === "boolean") {
    return (
      <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="font-medium">{label}</span>
      </label>
    );
  }

  if (usesRichTextEditor(field)) {
    return (
      <div className="block text-sm">
        <span className="font-medium text-slate-700">
          {label}
          {field.isRequired ? " *" : ""}
        </span>
        <RichTextEditor
          value={value ?? ""}
          onChange={onChange}
          placeholder={`Write ${label.toLowerCase()}...`}
          compact={isCompactRichTextField(field)}
        />
      </div>
    );
  }

  if (type === "image") {
    const src = resolveMediaUrl(value);
    return (
      <div className="space-y-2">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        {src ? (
          <img
            src={src}
            alt=""
            className="max-h-40 rounded-xl border border-gray-200 object-cover"
          />
        ) : null}
        {onUpload ? (
          <MediaUploadBox
            mediaType="image"
            busy={uploadBusy}
            onUpload={(file) => onUpload(file)}
          />
        ) : (
          <p className="text-sm text-slate-500">File upload is not available.</p>
        )}
      </div>
    );
  }

  if (type === "video" || type === "file") {
    const src = resolveMediaUrl(value);
    return (
      <div className="space-y-2">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        {src && type === "video" ? (
          <video
            src={src}
            controls
            className="max-h-48 w-full rounded-xl border border-gray-200"
          />
        ) : null}
        {src && type === "file" ? (
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-slate-600 underline"
          >
            {value}
          </a>
        ) : null}
        <input
          type="url"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="/uploads/..."
          className={inputClassName()}
        />
        {onUpload ? (
          <MediaUploadBox
            mediaType={type === "file" ? "document" : "video"}
            busy={uploadBusy}
            onUpload={(file) => onUpload(file)}
          />
        ) : null}
      </div>
    );
  }

  const inputType =
    type === "number"
      ? "number"
      : type === "email"
        ? "email"
        : type === "url"
          ? "url"
          : "text";

  return (
    <label className="block text-sm">
      <span className="font-medium text-slate-700">
        {label}
        {field.isRequired ? " *" : ""}
      </span>
      <input
        type={inputType}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={inputClassName()}
      />
    </label>
  );
}
